import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { Button } from "@/shared/components/ui/button";
import { FoundingReportFilters } from "./FoundingReportFilters";
import { FoundingAccountsSummary } from "./FoundingAccountsSummary";
import { FoundingPropertiesTable } from "./FoundingPropertiesTable";
import { FoundingPropertiesMap } from "./FoundingPropertiesMap";
import { AssignFoundingDialog } from "./AssignFoundingDialog";
import { UnassignFoundingDialog } from "./UnassignFoundingDialog";
import { useFoundingReport } from "./use-founding-report";

export function FoundingReportPage() {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  // Assign/unassign are WANDERFIT_ADMIN only at the API; the report also allows
  // WANDERFIT_FINANCE (read-only). The API is the real gate — this only hides
  // the affordances a non-admin operator can't use so they aren't left staring
  // at buttons that always 403.
  const canMutate = currentUser?.role === Role.WANDERFIT_ADMIN;
  const isReadOnly = currentUser?.role === Role.WANDERFIT_FINANCE;

  const [country, setCountry] = useState("");
  const [tier, setTier] = useState("");
  const [view, setView] = useState<"table" | "map">("table");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [unassignOpen, setUnassignOpen] = useState(false);

  const { data, isLoading, isFetching, isError } = useFoundingReport({
    country,
    tier,
  });

  const rows = data?.properties ?? [];
  const selectedIds = [...selected];

  // The result set changes when the filters change (keepPreviousData swaps the
  // rows under us). Reset the selection so we can never assign/unassign a
  // property that's no longer on screen — a permanent, billing-affecting action.
  useEffect(() => {
    setSelected(new Set());
  }, [country, tier]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = (checked: boolean) =>
    setSelected(checked ? new Set(rows.map((r) => r.propertyId)) : new Set());
  const clear = () => setSelected(new Set());

  return (
    <div className="space-y-6" data-testid="founding-report-page">
      <header>
        <h1 className="font-serif text-2xl font-semibold">{t("founding.title")}</h1>
        <p className="font-sans text-sm text-muted-foreground">{t("founding.subtitle")}</p>
      </header>

      {isReadOnly && (
        <p
          data-testid="founding-readonly-banner"
          role="status"
          className="rounded-md border border-border bg-muted/40 px-3 py-2 font-sans text-sm text-muted-foreground"
        >
          {t("founding.readOnlyBanner")}
        </p>
      )}

      <FoundingReportFilters
        country={country}
        tier={tier}
        onCountryChange={setCountry}
        onTierChange={setTier}
        onReset={() => {
          setCountry("");
          setTier("");
        }}
      />

      {data?.truncated && (
        <p
          data-testid="founding-truncated"
          className="rounded-md bg-amber-100 px-3 py-2 font-sans text-sm text-amber-800"
        >
          {t("founding.truncated")}
        </p>
      )}

      {isError ? (
        <p data-testid="founding-error" className="font-sans text-destructive">
          {t("founding.loadError")}
        </p>
      ) : isLoading ? (
        <p data-testid="founding-loading" className="font-sans text-muted-foreground">
          {t("founding.loading")}
        </p>
      ) : rows.length === 0 ? (
        <p data-testid="founding-empty" className="font-sans text-muted-foreground">
          {t("founding.empty")}
        </p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3">
            <FoundingAccountsSummary accounts={data?.accounts ?? []} />
            <div
              role="group"
              aria-label={t("founding.report.viewToggle")}
              className="inline-flex shrink-0 overflow-hidden rounded-md border border-border"
            >
              <button
                type="button"
                data-testid="founding-report-view-table"
                aria-pressed={view === "table"}
                onClick={() => setView("table")}
                className={
                  view === "table"
                    ? "bg-primary px-3 py-1.5 font-sans text-sm text-primary-foreground"
                    : "px-3 py-1.5 font-sans text-sm text-muted-foreground hover:bg-muted/40"
                }
              >
                {t("founding.report.viewTable")}
              </button>
              <button
                type="button"
                data-testid="founding-report-view-map"
                aria-pressed={view === "map"}
                onClick={() => setView("map")}
                className={
                  view === "map"
                    ? "bg-primary px-3 py-1.5 font-sans text-sm text-primary-foreground"
                    : "px-3 py-1.5 font-sans text-sm text-muted-foreground hover:bg-muted/40"
                }
              >
                {t("founding.report.viewMap")}
              </button>
            </div>
          </div>

          {canMutate && selected.size > 0 && (
            <div
              data-testid="founding-bulk-bar"
              className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2"
            >
              <span className="font-sans text-sm">
                {t("founding.bulk.selected", { count: selected.size })}
              </span>
              <Button
                type="button"
                size="sm"
                data-testid="founding-bulk-assign"
                onClick={() => setAssignOpen(true)}
              >
                {t("founding.bulk.assign")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="founding-bulk-unassign"
                onClick={() => setUnassignOpen(true)}
              >
                {t("founding.bulk.unassign")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                data-testid="founding-bulk-clear"
                onClick={clear}
              >
                {t("founding.bulk.clear")}
              </Button>
            </div>
          )}

          {view === "table" ? (
            <FoundingPropertiesTable
              rows={rows}
              selected={selected}
              selectable={canMutate}
              busy={isFetching}
              onToggle={toggle}
              onToggleAll={toggleAll}
            />
          ) : (
            <FoundingPropertiesMap rows={rows} />
          )}
        </>
      )}

      {canMutate && (
        <>
          <AssignFoundingDialog
            propertyIds={selectedIds}
            open={assignOpen}
            onOpenChange={setAssignOpen}
            onDone={clear}
          />
          <UnassignFoundingDialog
            propertyIds={selectedIds}
            open={unassignOpen}
            onOpenChange={setUnassignOpen}
            onDone={clear}
          />
        </>
      )}
    </div>
  );
}
